/* eslint-disable react/require-default-props */
import React from "react"
import { RouteComponentProps } from "@reach/router"
import {
  Card,
  DocumentTitleOverride,
  FullPageLoader,
  Separator,
  SubTitle,
  Flex,
  Box,
  ApiError,
} from "@shared/primitive-components"
import { FileTextIcon, InfoCircledIcon } from "@shared/primitive-components/src/Icons"
import { usePolicyDocuments } from "../api/policy/documents"
import { DocumentPortalTestIds } from "./types"
import * as Styles from "./styles"
import { urlToTitle } from "./utils"
import { usePolicyAndFleet } from "../contexts"
import { ScreenContentWithContext } from "../components/ScreenContentWithContext"
import { PrevPolicyDropdown } from "../ui/components"

const Container: React.FC = (props) => <Flex css={Styles.Container} {...props} />
type DocumentsType = {
  url: string
  key?: string
  title?: string
  dataTestId?: string
}
interface DocumentsProps extends RouteComponentProps {
  policyId?: string
}

export const Documents: React.FC<DocumentsProps> = ({ policyId }) => {
  const { policy } = usePolicyAndFleet()
  const [res] = usePolicyDocuments({ id: policyId ?? policy.id })
  const error = res.result?.tag === `err`
  if (error) return <ApiError error={res.result.error.type} data-testid={DocumentPortalTestIds.Error} />

  const documents: ReadonlyArray<DocumentsType> = [
    ...(res.result?.data ?? []),
    {
      title: `How to Report an Incident`,
      url: `https://static.flockcover.com/documents/how-to-report-an-incident.pdf`,
      dataTestId: DocumentPortalTestIds.ReportingIncident,
    },
    {
      title: `Driver Checklist`,
      url: `https://static.flockcover.com/documents/driver-checklist.pdf`,
      dataTestId: DocumentPortalTestIds.DriverChecklist,
    },
    {
      title: `The Importance of Early Notification`,
      url: `https://static.flockcover.com/documents/the-importance-of-early-notification.pdf`,
      dataTestId: DocumentPortalTestIds.ImportanceOfEarlyNotification,
    },
  ]

  return (
    <>
      <DocumentTitleOverride>Flock Motor | Documents</DocumentTitleOverride>
      <ScreenContentWithContext
        title="Documents"
        dataTestIds={{ root: DocumentPortalTestIds.Portal }}
        PreviousPolicyDropdown={<PrevPolicyDropdown />}
      >
        {res.loading ? (
          <FullPageLoader data-testid={DocumentPortalTestIds.Loader} />
        ) : (
          <Card>
            <Container>
              <SubTitle>
                Your Policy Documents
                <Separator size="block" color="yellow" />
              </SubTitle>

              <Box css={Styles.List}>
                {documents && documents.length > 0 ? (
                  documents.map(({ url, title, key, dataTestId }) => (
                    <a href={url} target="_blank" rel="noreferrer" key={url}>
                      <Box css={Styles.ListItem} data-testid={dataTestId}>
                        <FileTextIcon />
                        {key !== undefined ? urlToTitle(key) : title}
                      </Box>
                    </a>
                  ))
                ) : (
                  <Box css={Styles.ListItem} style={{ cursor: `unset` }} data-testid={DocumentPortalTestIds.Empty}>
                    <InfoCircledIcon />
                    No documents found
                  </Box>
                )}
              </Box>
            </Container>
          </Card>
        )}
      </ScreenContentWithContext>
    </>
  )
}
